import React, { createContext, useCallback, useReducer } from 'react';
import { ClientSongCollection, getClientSongCollections } from '@round/api';
import { createReducer } from '../../../../helpers';
import { ReducerAction, ReducerActionWithPayload } from '../../../../App.types';
import useNonNullContext from '../../../../Hooks/useNonNullContext';

type ClientSongCollectionsContextState = {
    collections: ClientSongCollection[];
    count: number;
    initiated: boolean;
    search: string;
    loading: boolean;
    hasNextPage: boolean;
    page: number;
    hasErrorLoading: boolean;
};

const initialState: ClientSongCollectionsContextState = {
    initiated: false,
    collections: [],
    count: 0,
    search: '',
    loading: false,
    hasNextPage: false,
    page: 1,
    hasErrorLoading: false,
};

type Actions =
    | ReducerAction<'initiate'>
    | ReducerActionWithPayload<
          'initiated',
          Pick<ClientSongCollectionsContextState, 'count' | 'collections' | 'search' | 'hasNextPage'>
      >
    | ReducerAction<'loadNextPage'>
    | ReducerActionWithPayload<
          'nextPageLoaded',
          Pick<ClientSongCollectionsContextState, 'count' | 'collections' | 'hasNextPage'>
      >
    | ReducerAction<'errorLoading'>;

const setLoading = (state: ClientSongCollectionsContextState) => ({ ...state, loading: true, hasErrorLoading: false });
const reducer = createReducer<ClientSongCollectionsContextState, Actions>({
    initiate: setLoading,
    initiated: (state, { payload }) => ({ ...state, initiated: true, loading: false, ...payload }),
    loadNextPage: setLoading,
    nextPageLoaded: (state, { payload }) => ({
        ...state,
        ...payload,
        loading: false,
        page: state.page + 1,
        collections: state.collections.concat(payload.collections),
    }),
    errorLoading: (state) => ({ ...state, hasErrorLoading: true, loading: false }),
});

const Context = createContext<[ClientSongCollectionsContextState, React.Dispatch<Actions>] | null>(null);
export const ClientSongCollectionsProvider = ({ children }: { children: React.ReactNode | undefined }) => {
    const [state, dispatch] = useReducer(reducer, initialState);
    return <Context.Provider value={[state, dispatch]}>{children}</Context.Provider>;
};

const PAGE_SIZE = 25;
export function useClientSongCollectionOptions(clientId: number) {
    const [state, dispatch] = useNonNullContext(Context);

    const initiate = useCallback(
        async (search: string, requestInit?: RequestInit) => {
            try {
                dispatch({ type: 'initiate' });
                const response = await getClientSongCollections(
                    { clientId, page: 1, page_size: PAGE_SIZE, search },
                    requestInit
                );
                if (response.status === 200) {
                    dispatch({
                        payload: {
                            collections: response.data.results,
                            count: response.data.count,
                            search,
                            hasNextPage: typeof response.data.next === 'string',
                        },
                        type: 'initiated',
                    });
                    return;
                }

                dispatch({ type: 'errorLoading' });
            } catch (e) {
                if (e instanceof Error && e.name === 'AbortError') {
                    return;
                }

                dispatch({ type: 'errorLoading' });
            }
        },
        [clientId, dispatch]
    );

    const loadMore = useCallback(async () => {
        try {
            dispatch({ type: 'loadNextPage' });
            const response = await getClientSongCollections({
                clientId,
                page: state.page + 1,
                page_size: PAGE_SIZE,
                search: state.search,
            });

            if (response.status === 200) {
                dispatch({
                    type: 'nextPageLoaded',
                    payload: {
                        collections: response.data.results,
                        hasNextPage: typeof response.data.next === 'string',
                        count: response.data.count,
                    },
                });
                return;
            }

            dispatch({ type: 'errorLoading' });
        } catch {
            dispatch({ type: 'errorLoading' });
        }
    }, [clientId, dispatch, state.page, state.search]);

    return { ...state, initiate, loadMore };
}
